<template>
  <div class="lc-detail-view" v-loading="loading">
    <!-- 헤더 -->
    <div class="page-header">
      <div class="header-left">
        <el-button link @click="goBack">
          <el-icon><ArrowLeft /></el-icon>
          목록으로
        </el-button>
        <h2>{{ lc?.lc_number }}</h2>
        <el-tag :type="getStatusType(lc?.status)" size="large">
          {{ lc?.status_display }}
        </el-tag>
      </div>
      <div class="header-right">
        <el-dropdown v-if="userStore.isLCManager" @command="handleStatusAction">
          <el-button type="primary">
            상태변경
            <el-icon class="el-icon--right"><ArrowDown /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-if="lc?.status === 'DRAFT'"
                command="request_issue"
              >
                개설 요청
              </el-dropdown-item>
              <el-dropdown-item
                v-if="lc?.status === 'DRAFT' || lc?.status === 'REQUESTED'"
                command="confirm_issue"
              >
                개설 완료
              </el-dropdown-item>
              <el-dropdown-item
                v-if="lc?.status === 'ISSUED' || lc?.status === 'AMENDED'"
                command="mark_shipped"
              >
                선적 완료
              </el-dropdown-item>
              <el-dropdown-item
                v-if="lc?.status === 'SHIPPED'"
                command="mark_negotiated"
              >
                매입 완료
              </el-dropdown-item>
              <el-dropdown-item
                v-if="lc?.status === 'NEGOTIATED'"
                command="mark_settled"
              >
                결제 완료
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <el-button
          v-if="userStore.isLCManager && !['SETTLED', 'CANCELLED'].includes(lc?.status)"
          @click="openEditDialog"
        >
          <el-icon><Edit /></el-icon>
          수정
        </el-button>
        <el-button @click="showHelp">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <!-- 요약 영역 (재설계 Stage 1) -->
    <div class="lc-summary" v-if="lc">
      <div class="lc-cta-row" v-if="nextAction && userStore.isLCManager">
        <el-button type="primary" @click="handleStatusAction(nextAction.cmd)">
          다음 단계: {{ nextAction.label }}
        </el-button>
      </div>

      <div class="lc-stepper">
        <template v-for="(s, i) in stages" :key="s.label">
          <div class="lc-step" :class="s.state">
            <div class="dot">
              <el-icon v-if="s.state === 'done'"><Check /></el-icon>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <div class="lbl">{{ s.label }}</div>
            <div class="dt">{{ s.date || '—' }}</div>
          </div>
          <div v-if="i < stages.length - 1" class="lc-line" :class="{ done: s.state === 'done' }"></div>
        </template>
      </div>

      <div class="lc-kpi">
        <div class="kpi">
          <div class="k">LC 금액</div>
          <div class="v">{{ lc.currency }} {{ formatNumber(lc.amount) }}</div>
        </div>
        <div class="kpi">
          <div class="k">미청구 잔액</div>
          <div class="v">{{ lc.progress ? lc.currency + ' ' + formatNumber(lc.progress.remaining_uninvoiced) : '-' }}</div>
        </div>
        <div class="kpi">
          <div class="k">잔여한도</div>
          <div class="v">{{ lc.facility_limit ? lc.facility_limit.currency + ' ' + formatNumber(lc.facility_limit.available) : '-' }}</div>
        </div>
        <div class="kpi">
          <div class="k">상환만기</div>
          <div class="v" :class="ddayClass(repayDays)">{{ lc.payment_due_date ? ddayLabel(repayDays) + ' · ' + lc.payment_due_date : '-' }}</div>
        </div>
        <div class="kpi">
          <div class="k">유효기일</div>
          <div class="v" :class="ddayClass(lc.days_until_expiry)">{{ lc.expiry_date ? ddayLabel(lc.days_until_expiry) + ' · ' + lc.expiry_date : '-' }}</div>
        </div>
      </div>

      <div class="lc-progress" v-if="lc.progress">
        <div class="prg">
          <div class="prg-head">
            <span>청구 (송장)</span>
            <span class="prg-num">{{ lc.progress.invoice_count }}건 · {{ lc.currency }} {{ formatNumber(lc.progress.invoiced_amount) }} / {{ formatNumber(lc.progress.lc_amount) }} · 잔액 <b>{{ formatNumber(lc.progress.remaining_uninvoiced) }}</b></span>
          </div>
          <el-progress :percentage="Math.min(100, lc.progress.invoiced_pct)" :stroke-width="10" :format="() => lc.progress.invoiced_pct + '%'" />
        </div>
        <!-- 서류 인수 진행 (2026-07-09) — 파샬 선적분(송장)별 인수 구분 -->
        <div class="prg" v-if="lc.progress.invoice_count">
          <div class="prg-head">
            <span>서류 인수</span>
            <span class="prg-num">{{ lc.progress.accepted_count }}/{{ lc.progress.invoice_count }}건 · {{ lc.currency }} {{ formatNumber(lc.progress.accepted_amount) }} · 미인수 <b>{{ formatNumber(lc.progress.unaccepted_amount) }}</b></span>
          </div>
          <el-progress :percentage="Math.min(100, lc.progress.accepted_pct)" :stroke-width="10" color="#e6a23c" :format="() => lc.progress.accepted_pct + '%'" />
        </div>
        <div class="prg">
          <div class="prg-head">
            <span>상환</span>
            <span class="prg-num">{{ lc.progress.settle_done }}/{{ lc.progress.settle_total }}건 · {{ lc.currency }} {{ formatNumber(lc.progress.settled_amount) }} / {{ formatNumber(lc.progress.lc_amount) }}</span>
          </div>
          <el-progress :percentage="Math.min(100, lc.progress.settled_pct)" :stroke-width="10" color="#67c23a" :format="() => lc.progress.settled_pct + '%'" />
        </div>
      </div>

      <div v-for="(al, idx) in alerts" :key="idx" class="lc-alert" :class="al.type">
        <el-icon><WarningFilled /></el-icon> <span>{{ al.text }}</span>
      </div>
    </div>

    <!-- 기본 정보 -->
    <el-row :gutter="20">
      <el-col :span="16">
        <el-tabs v-model="activeTab" class="lc-tabs">
        <el-tab-pane label="개요" name="overview">
        <el-card>
          <template #header>기본 정보</template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="LC번호">{{ lc?.lc_number }}</el-descriptions-item>
            <el-descriptions-item label="법인">{{ lc?.company_name || '-' }}</el-descriptions-item>
            <el-descriptions-item label="유형">{{ lc?.lc_type_display }}</el-descriptions-item>
            <el-descriptions-item label="메인오더">{{ lc?.main_request_number }}</el-descriptions-item>
            <el-descriptions-item label="상태">
              <el-tag :type="getStatusType(lc?.status)">{{ lc?.status_display }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="금액">
              {{ lc?.currency }} {{ formatNumber(lc?.amount) }}
            </el-descriptions-item>
            <el-descriptions-item label="과부족 허용">
              +{{ lc?.tolerance_plus }}% / -{{ lc?.tolerance_minus }}%
            </el-descriptions-item>
            <el-descriptions-item label="최대금액">
              {{ lc?.currency }} {{ formatNumber(lc?.max_amount) }}
            </el-descriptions-item>
            <el-descriptions-item label="최소금액">
              {{ lc?.currency }} {{ formatNumber(lc?.min_amount) }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card style="margin-top: 20px">
          <template #header>당사자 정보</template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="개설은행">{{ lc?.issuing_bank }}</el-descriptions-item>
            <el-descriptions-item label="통지은행">{{ lc?.advising_bank || '-' }}</el-descriptions-item>
            <el-descriptions-item label="매입은행">{{ lc?.negotiating_bank || '-' }}</el-descriptions-item>
            <el-descriptions-item label=""></el-descriptions-item>
            <el-descriptions-item label="개설의뢰인">{{ lc?.applicant }}</el-descriptions-item>
            <el-descriptions-item label="수익자">{{ lc?.beneficiary }}</el-descriptions-item>
            <el-descriptions-item label="공급사">{{ lc?.supplier_name || '-' }}</el-descriptions-item>
            <el-descriptions-item label=""></el-descriptions-item>
          </el-descriptions>
        </el-card>

        </el-tab-pane>
        <el-tab-pane label="선적·서류" name="ship">
        <el-card>
          <template #header>선적/납기 정보</template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="선적항">{{ lc?.port_of_loading || '-' }}</el-descriptions-item>
            <el-descriptions-item label="양륙항">{{ lc?.port_of_discharge || '-' }}</el-descriptions-item>
            <el-descriptions-item label="분할선적">{{ lc?.partial_shipment ? '허용' : '불허' }}</el-descriptions-item>
            <el-descriptions-item label="환적">{{ lc?.transshipment ? '허용' : '불허' }}</el-descriptions-item>
            <el-descriptions-item label="최종선적일">{{ lc?.latest_shipment_date || '-' }}</el-descriptions-item>
            <el-descriptions-item label="선적까지">
              <el-tag
                v-if="lc && lc.days_until_shipment != null"
                :type="lc.days_until_shipment < 0 ? 'danger' : lc.days_until_shipment <= 7 ? 'warning' : 'info'"
                size="small"
              >
                {{ lc.days_until_shipment < 0 ? `D+${Math.abs(lc.days_until_shipment)}` : `D-${lc.days_until_shipment}` }}
              </el-tag>
              <span v-else>-</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card style="margin-top: 20px">
          <template #header>
            수입송장
            <!-- 이 LC의 수입송장 바로 입력 (2026-07-14 요청 #3) — 수입송장 화면으로 이동해 LC·법인 프리셋으로 신규 다이얼로그 자동 오픈 -->
            <el-button
              v-if="userStore.isLCManager"
              size="small"
              type="primary"
              style="float: right"
              @click="goNewInvoice"
            >
              수입송장 입력
            </el-button>
          </template>
          <el-table v-if="lc?.invoices?.length" :data="lc.invoices" size="small">
            <el-table-column label="송장번호" prop="invoice_number" min-width="130" />
            <el-table-column label="송장일" prop="invoice_date" width="110" />
            <el-table-column label="금액" align="right">
              <template #default="{ row }">
                {{ row.currency }} {{ Number(row.amount || 0).toLocaleString() }}
              </template>
            </el-table-column>
            <el-table-column label="도착예정(ETA)" prop="eta" width="120" />
            <el-table-column label="B/L번호" prop="bl_number" min-width="110" />
            <!-- 서류 인수 (2026-07-09) — 선적분별 인수 구분, USANCE 만기는 인수일 기준 재계산 -->
            <el-table-column label="인수" width="120">
              <template #default="{ row }">
                <el-tag v-if="row.acceptance_date" size="small" type="success">{{ row.acceptance_date }}</el-tag>
                <el-button v-else-if="userStore.isLCManager" size="small" text type="primary" @click="acceptInvoice(row)">인수 처리</el-button>
                <el-tag v-else size="small" type="info" effect="plain">미인수</el-tag>
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-else description="등록된 수입송장이 없습니다" :image-size="60" />
        </el-card>

        <!-- 매입 차수 (2026-07-14) — 유산스이자는 같은 '매입일' 묶음에 부과(업무 확인).
             차수별 원금(Σ송장)·이자·서류를 관리하고 상환은 차수당 1건(원금+이자, LC통화) -->
        <el-card style="margin-top: 20px">
          <template #header>
            매입 차수 (유산스이자·서류)
            <el-button
              v-if="userStore.isLCManager"
              size="small"
              type="primary"
              style="float: right"
              @click="addNegotiation"
            >
              차수 추가
            </el-button>
          </template>
          <el-table v-if="lc?.negotiations?.length" :data="lc.negotiations" size="small">
            <el-table-column label="매입일" prop="negotiation_date" width="105" />
            <el-table-column label="원금" align="right" min-width="120">
              <template #default="{ row }">
                {{ row.currency }} {{ formatNumber(row.principal) }}
                <el-tooltip v-if="row.invoices?.length" placement="top"
                  :content="row.invoices.map(i => `${i.invoice_number} (${formatNumber(i.amount)})`).join(', ')">
                  <span class="neg-inv-cnt">송장 {{ row.invoices.length }}건</span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="유산스이자" align="right" width="130">
              <template #default="{ row }">
                <span v-if="row.usance_interest">{{ formatNumber(row.usance_interest) }}</span>
                <span v-else class="neg-none">-</span>
                <el-button v-if="userStore.isLCManager" size="small" text type="primary"
                  @click="editInterest(row)">입력</el-button>
              </template>
            </el-table-column>
            <el-table-column label="상환 (원금+이자)" min-width="170">
              <template #default="{ row }">
                <template v-if="row.settlement">
                  {{ row.settlement.currency }} {{ formatNumber(row.settlement.fx_amount) }}
                  · {{ row.settlement.planned_date }}
                  <el-tag size="small"
                    :type="row.settlement.status === 'EXECUTED' ? 'success' : 'warning'">
                    {{ row.settlement.status_display }}
                  </el-tag>
                </template>
                <span v-else class="neg-none">만기 {{ row.settlement_due_date || '-' }} · 미생성</span>
              </template>
            </el-table-column>
            <el-table-column label="서류" width="90">
              <template #default="{ row }">
                <el-button size="small" text type="primary" @click="openNegDocs(row)">
                  {{ row.documents?.length || 0 }}건 관리
                </el-button>
              </template>
            </el-table-column>
            <el-table-column label="" width="60">
              <template #default="{ row }">
                <el-button
                  v-if="userStore.isLCManager && row.settlement?.status !== 'EXECUTED'"
                  size="small" type="danger" link @click="deleteNegotiation(row)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-else description="매입 차수가 없습니다 — 송장 '인수 처리' 시 매입일 기준으로 자동 생성됩니다" :image-size="60" />
          <div class="neg-hint">유산스이자는 같은 매입일 묶음에 부과됩니다 — 이자 입력 시 상환액(원금+이자)이 자동 갱신되어 자금계획에 반영됩니다. 이자가 없으면 원금만 상환됩니다.</div>
        </el-card>

        </el-tab-pane>
        <el-tab-pane label="자금·상환" name="finance">
        <el-card>
          <template #header>비용/환율 정보</template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="개설환율">{{ lc?.opening_exchange_rate || '-' }}</el-descriptions-item>
            <el-descriptions-item label="상환금액">{{ formatNumber(lc?.repayment_amount) }}</el-descriptions-item>
            <el-descriptions-item label="유산스이자">{{ formatNumber(lc?.usance_interest) }}</el-descriptions-item>
            <el-descriptions-item label="인수수수료">{{ formatNumber(lc?.acceptance_fee) }}</el-descriptions-item>
            <el-descriptions-item label="개설수수료">{{ formatNumber(lc?.opening_fee) }}</el-descriptions-item>
            <el-descriptions-item label="조건변경수수료">{{ formatNumber(lc?.amendment_fee) }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card style="margin-top: 20px">
          <template #header>여신한도 · 상환</template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="한도계좌">{{ lc?.facility_account_name || '-' }}</el-descriptions-item>
            <el-descriptions-item label="설정한도">
              <span v-if="lc?.facility_limit">{{ formatNumber(lc.facility_limit.limit) }} {{ lc.facility_limit.currency }}</span>
              <span v-else>-</span>
            </el-descriptions-item>
            <el-descriptions-item label="사용한도">
              <span v-if="lc?.facility_limit">{{ formatNumber(lc.facility_limit.used) }} {{ lc.facility_limit.currency }}</span>
              <span v-else>-</span>
            </el-descriptions-item>
            <el-descriptions-item label="잔여한도">
              <span v-if="lc?.facility_limit" :style="{ color: lc.facility_limit.over_limit ? '#f56c6c' : '#67c23a' }">
                {{ formatNumber(lc.facility_limit.available) }} {{ lc.facility_limit.currency }}
              </span>
              <span v-else>-</span>
            </el-descriptions-item>
          </el-descriptions>
          <el-alert v-if="lc?.facility_limit?.over_limit" type="error" :closable="false" show-icon
            title="여신한도를 초과했습니다." style="margin-top: 8px" />
          <el-descriptions :column="2" border style="margin-top: 12px">
            <el-descriptions-item label="상환원금">
              {{ formatNumber(settlement?.principal_fx) }} {{ settlement?.currency }}
            </el-descriptions-item>
            <el-descriptions-item label="적용환율">{{ settlement?.applied_rate || '-' }}</el-descriptions-item>
            <el-descriptions-item label="유산스이자">{{ formatNumber(settlement?.usance_interest) }}</el-descriptions-item>
            <el-descriptions-item label="인수수수료">{{ formatNumber(settlement?.acceptance_fee) }}</el-descriptions-item>
            <el-descriptions-item label="상환액(KRW)">{{ formatNumber(settlement?.total_krw) }}</el-descriptions-item>
            <el-descriptions-item label="추가자금필요">
              {{ formatNumber(settlement?.funding_gap) }} {{ settlement?.currency }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card style="margin-top: 20px">
          <template #header>상환·수수료 자금계획</template>
          <el-table v-if="lc?.payments?.length" :data="lc.payments" size="small">
            <el-table-column label="유형" prop="kind_display" width="120" />
            <el-table-column label="예정일" prop="planned_date" width="110" />
            <el-table-column label="금액" align="right">
              <template #default="{ row }">
                {{ row.currency }} {{ Number(row.fx_amount || row.total_amount || 0).toLocaleString() }}
              </template>
            </el-table-column>
            <el-table-column label="상태" width="90">
              <template #default="{ row }">
                <el-tag size="small" :type="row.status === 'EXECUTED' ? 'success' : (row.status === 'CANCELLED' ? 'info' : 'warning')">
                  {{ row.status_display }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="지급번호" prop="payment_number" min-width="140" />
          </el-table>
          <el-empty v-else description="연결된 자금계획이 없습니다 (개설 확정 시 자동 생성)" :image-size="60" />
        </el-card>

        </el-tab-pane>
        <el-tab-pane label="비용·보험" name="cost">
        <el-card>
          <template #header>보험 정보</template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="적하보험료">{{ formatNumber(lc?.cargo_insurance_premium) }}</el-descriptions-item>
            <el-descriptions-item label="적하보험 증권번호">{{ lc?.cargo_insurance_policy_number || '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card style="margin-top: 20px">
          <template #header>무역조건 정보</template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="가격조건">{{ lc?.price_terms || '-' }}</el-descriptions-item>
            <el-descriptions-item label="지출결의서" :span="2">
              <template v-if="lc?.vouchers && lc.vouchers.length">
                <div v-for="v in lc.vouchers" :key="v.document_number" class="voucher-line">
                  <span class="v-doc">{{ v.document_number }}</span>
                  <span class="v-amt">{{ v.currency }} {{ formatNumber(v.display_amount) }}</span>
                  <el-tag size="small" :type="v.status === 'completed' ? 'success' : (v.status === 'processing' ? 'warning' : 'info')">{{ v.status_display }}</el-tag>
                </div>
              </template>
              <template v-else>{{ lc?.expense_voucher_number || '지출결의서 없이 개설' }}</template>
            </el-descriptions-item>
            <el-descriptions-item label="선적시작일">{{ lc?.shipment_date_start || '-' }}</el-descriptions-item>
            <el-descriptions-item label="선적종료일">{{ lc?.shipment_date_end || '-' }}</el-descriptions-item>
            <el-descriptions-item label="LG 원본회수">{{ lc?.lg_original_recovered ? '회수' : '미회수' }}</el-descriptions-item>
            <el-descriptions-item label="비고">{{ lc?.remarks || '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>
        </el-tab-pane>
        </el-tabs>
      </el-col>

      <el-col :span="8">
        <el-card>
          <template #header>일정 정보</template>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="개설일">{{ lc?.issue_date || '-' }}</el-descriptions-item>
            <el-descriptions-item label="유효기일">{{ lc?.expiry_date }}</el-descriptions-item>
            <el-descriptions-item label="유효기일까지">
              <el-tag
                v-if="lc && lc.days_until_expiry != null"
                :type="lc.is_expired ? 'danger' : lc.days_until_expiry <= 7 ? 'warning' : 'info'"
                size="small"
              >
                {{ lc.is_expired ? '만료' : `D-${lc.days_until_expiry}` }}
              </el-tag>
              <span v-else>-</span>
            </el-descriptions-item>
            <el-descriptions-item v-if="lc?.lc_type === 'USANCE'" label="유산스 기간">
              {{ lc?.usance_days }}일
            </el-descriptions-item>
            <el-descriptions-item v-if="lc?.payment_due_date" label="결제예정일">
              {{ lc?.payment_due_date }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card style="margin-top: 20px">
          <template #header>
            조건변경 내역
            <!-- 개설완료~상환 전이면 어멘드 가능 — 선적/인수 후 SD·ED 연장·증액, 만료 LC의 ED 연장 포함 (2026-07-09) -->
            <el-button
              v-if="userStore.isLCManager && ['ISSUED', 'AMENDED', 'SHIPPED', 'NEGOTIATED'].includes(lc?.status)"
              size="small"
              type="primary"
              style="float: right"
              @click="openAmendmentDialog"
            >
              조건변경 추가
            </el-button>
          </template>
          <el-timeline v-if="lc?.amendments?.length > 0">
            <el-timeline-item
              v-for="amendment in lc.amendments"
              :key="amendment.id"
              :timestamp="amendment.amendment_date"
              placement="top"
            >
              <el-card>
                <strong>#{{ amendment.amendment_number }}</strong>
                <div v-if="amendment.changed_fields && Object.keys(amendment.changed_fields).length" class="amd-changes">
                  <div v-for="(c, f) in amendment.changed_fields" :key="f" class="amd-change">
                    <span class="amd-label">{{ c.label || f }}</span>
                    <span class="amd-old">{{ fmtAmdVal(c.old) }}</span>
                    <span class="amd-arrow">→</span>
                    <span class="amd-new">{{ fmtAmdVal(c.new) }}</span>
                  </div>
                </div>
                <p v-else>{{ amendment.description }}</p>
              </el-card>
            </el-timeline-item>
          </el-timeline>
          <el-empty v-else description="조건변경 내역이 없습니다" :image-size="60" />
        </el-card>
      </el-col>
    </el-row>

    <!-- 조건변경 다이얼로그 (구조화) -->
    <el-dialog v-model="showAmendmentDialog" title="조건변경 추가" width="640px">
      <el-form :model="amendmentForm" label-width="80px">
        <el-form-item label="변경일">
          <el-date-picker
            v-model="amendmentForm.amendment_date"
            type="date"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
      </el-form>
      <p class="amd-hint">변경할 항목만 입력하세요. 빈 칸은 변경하지 않습니다.</p>
      <div class="amd-grid">
        <div class="amd-grid-head">
          <span>항목</span><span>현재값</span><span>변경값</span>
        </div>
        <div v-for="f in amendableFields" :key="f.key" class="amd-grid-row">
          <span class="amd-fld">{{ f.label }}</span>
          <span class="amd-cur">{{ curDisplay(f) }}</span>
          <span class="amd-inp">
            <el-input-number v-if="f.type === 'number'" v-model="amendmentForm.changes[f.key]"
              :min="0" :controls="false" placeholder="변경 안 함" style="width:100%" />
            <el-date-picker v-else-if="f.type === 'date'" v-model="amendmentForm.changes[f.key]"
              type="date" value-format="YYYY-MM-DD" placeholder="변경 안 함" style="width:100%" />
            <el-select v-else-if="f.type === 'bool'" v-model="amendmentForm.changes[f.key]"
              clearable placeholder="변경 안 함" style="width:100%">
              <el-option label="허용" value="true" />
              <el-option label="불가" value="false" />
            </el-select>
            <el-input v-else v-model="amendmentForm.changes[f.key]" placeholder="변경 안 함" />
          </span>
        </div>
      </div>
      <el-form :model="amendmentForm" label-width="80px" style="margin-top:12px">
        <el-form-item label="변경사유">
          <el-input v-model="amendmentForm.description" type="textarea" :rows="2"
            placeholder="비워두면 변경내역으로 자동 생성됩니다" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showAmendmentDialog = false">취소</el-button>
        <el-button type="primary" @click="addAmendment">저장</el-button>
      </template>
    </el-dialog>

    <!-- 매입 서류 다이얼로그 (2026-07-14) — 파샬 매입분(차수)별 서류철 -->
    <el-dialog v-model="negDocVisible" :title="`매입 서류 — ${curNeg?.negotiation_date || ''} 매입분`" width="680px">
      <el-table v-if="negDocs.length" :data="negDocs" size="small">
        <el-table-column label="유형" prop="document_type_display" width="140" />
        <el-table-column label="서류번호" prop="document_number" min-width="120" />
        <el-table-column label="발행일" prop="issue_date" width="105" />
        <el-table-column label="파일" width="90">
          <template #default="{ row }">
            <a v-if="row.file" :href="row.file" target="_blank">열기</a>
            <span v-else class="neg-none">-</span>
          </template>
        </el-table-column>
        <el-table-column label="" width="60">
          <template #default="{ row }">
            <el-button v-if="userStore.isLCManager" size="small" type="danger" link
              @click="deleteNegDoc(row)">삭제</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-else description="등록된 서류가 없습니다" :image-size="50" />

      <template v-if="userStore.isLCManager">
        <el-divider content-position="left">서류 추가</el-divider>
        <div class="neg-doc-form">
          <el-select v-model="negDocForm.document_type" style="width: 160px">
            <el-option label="B/L" value="BL" />
            <el-option label="Commercial Invoice" value="INVOICE" />
            <el-option label="Packing List" value="PACKING_LIST" />
            <el-option label="인수통지서" value="ACCEPTANCE_ADVICE" />
            <el-option label="이자 계산서" value="INTEREST_NOTICE" />
            <el-option label="기타" value="OTHER" />
          </el-select>
          <el-input v-model="negDocForm.document_number" placeholder="서류번호" style="width: 150px" />
          <el-date-picker v-model="negDocForm.issue_date" type="date" value-format="YYYY-MM-DD"
            placeholder="발행일" style="width: 140px" />
          <input ref="negDocFileRef" type="file" class="neg-doc-file" @change="onNegDocFile" />
          <el-button type="primary" size="small" :loading="negDocUploading" @click="uploadNegDoc">추가</el-button>
        </div>
      </template>
      <template #footer>
        <el-button @click="closeNegDocs">닫기</el-button>
      </template>
    </el-dialog>

    <!-- LC 수정 다이얼로그 -->
    <LCFormDialog
      v-if="lc"
      v-model="showEditDialog"
      :edit-data="lc"
      @saved="onEdited"
    />

    <!-- 도움말 패널 -->
    <HelpPanel
      v-model="helpVisible"
      :content-path="'screens/lc-detail'"
    />
  </div>
</template>

<script setup>
import {ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { ArrowLeft, ArrowDown, QuestionFilled, Check, WarningFilled, Edit } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'
import HelpPanel from '@/components/help/HelpPanel.vue'
import LCFormDialog from '@/components/trade/LCFormDialog.vue'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

// ⚠ keep-alive 함정 픽스 (2026-08-03): 전역 route.params.id 를 computed 로
// 읽으면 다른 :id 라우트로 이동한 순간 살아있는 이 화면이 그 id 를 자기 것으로
// 착각해 엉뚱한 조회(404 폭탄, id 충돌 시 다른 문서 표시 위험).
// 이 화면 라우트일 때만 갱신하고, 재진입으로 id 가 바뀌면 재조회한다.
const lcId = ref(route.params.id)
watch(() => route.fullPath, () => {
  if (route.path.startsWith('/lcs/') && route.params.id
      && route.params.id !== lcId.value) {
    lcId.value = route.params.id
    fetchData()
  }
})
const loading = ref(false)
const lc = ref(null)
const settlement = ref(null)
const activeTab = ref('overview')
const helpVisible = ref(false)
const showEditDialog = ref(false)
const showAmendmentDialog = ref(false)

// 조건변경 가능 항목 (백엔드 LC.AMENDABLE_FIELDS와 일치)
const amendableFields = [
  { key: 'amount', label: '금액', type: 'number' },
  { key: 'expiry_date', label: '유효기일', type: 'date' },
  { key: 'latest_shipment_date', label: '최종선적일', type: 'date' },
  { key: 'partial_shipment', label: '분할선적', type: 'bool' },
  { key: 'transshipment', label: '환적', type: 'bool' },
  { key: 'usance_days', label: '유산스 일수', type: 'number' },
  { key: 'goods_description', label: '상품명세', type: 'text' },
  { key: 'beneficiary', label: '수익자', type: 'text' },
]
const blankChanges = () => Object.fromEntries(amendableFields.map(f => [f.key, undefined]))
const amendmentForm = reactive({
  amendment_date: new Date().toISOString().split('T')[0],
  description: '',
  changes: blankChanges(),
})
// 현재값 표시 (bool→허용/불가)
const curDisplay = (f) => {
  const v = lc.value?.[f.key]
  if (f.type === 'bool') return v ? '허용' : '불가'
  if (v === null || v === undefined || v === '') return '-'
  if (f.type === 'number') return Number(v).toLocaleString()
  return v
}
// 이력 old/new 표시 (bool 문자열 대응)
const fmtAmdVal = (v) => {
  if (v === true || v === 'true') return '허용'
  if (v === false || v === 'false') return '불가'
  if (v === null || v === undefined || v === '') return '-'
  if (typeof v === 'number' || (/^\d+(\.\d+)?$/.test(String(v)))) return Number(v).toLocaleString()
  return v
}

const getStatusType = (status) => {
  const types = {
    'DRAFT': 'info',
    'REQUESTED': 'warning',
    'ISSUED': '',
    'AMENDED': 'warning',
    'SHIPPED': 'success',
    'NEGOTIATED': 'success',
    'SETTLED': 'info',
    'CANCELLED': 'danger'
  }
  return types[status] || 'info'
}

const formatNumber = (num) => {
  const n = Number(num)
  if (num === null || num === undefined || num === '' || Number.isNaN(n)) return '0'
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

// ── 재설계 Stage 1: 요약/스테퍼/KPI ──
const STAGES = ['개설', '선적', '서류인수', '만기', '상환']
const STATUS_STAGE = { DRAFT: 0, REQUESTED: 0, ISSUED: 1, AMENDED: 1, SHIPPED: 2, NEGOTIATED: 3, SETTLED: 4 }

const stages = computed(() => {
  const cur = STATUS_STAGE[lc.value?.status] ?? 0
  const dates = [
    lc.value?.issue_date, lc.value?.latest_shipment_date, null,
    lc.value?.payment_due_date, null,
  ]
  return STAGES.map((label, i) => ({
    label,
    date: dates[i],
    state: i < cur ? 'done' : (i === cur ? 'current' : 'future'),
  }))
})

const nextAction = computed(() => {
  const m = {
    // 초안 → 바로 개설 완료 (2026-07-14 담당자 요청 — 개설요청 클릭 생략, 자동 경유)
    DRAFT: { label: '개설 완료', cmd: 'confirm_issue' },
    REQUESTED: { label: '개설 완료', cmd: 'confirm_issue' },
    ISSUED: { label: '선적 완료', cmd: 'mark_shipped' },
    AMENDED: { label: '선적 완료', cmd: 'mark_shipped' },
    SHIPPED: { label: '매입 완료', cmd: 'mark_negotiated' },
    NEGOTIATED: { label: '결제 완료', cmd: 'mark_settled' },
  }
  return m[lc.value?.status] || null
})

const daysUntil = (dateStr) => {
  if (!dateStr) return null
  return Math.ceil((new Date(dateStr) - new Date(new Date().toDateString())) / 86400000)
}
const repayDays = computed(() => daysUntil(lc.value?.payment_due_date))
const ddayLabel = (d) => d == null ? '-' : (d < 0 ? `D+${Math.abs(d)}` : `D-${d}`)
const ddayClass = (d) => d == null ? '' : (d < 0 ? 'dday-danger' : (d <= 7 ? 'dday-warn' : ''))

const alerts = computed(() => {
  const a = []
  const l = lc.value
  if (!l) return a
  if (l.facility_limit?.over_limit) a.push({ type: 'error', text: '여신한도를 초과했습니다.' })
  if (l.is_expired && !['SETTLED', 'CANCELLED'].includes(l.status)) {
    a.push({ type: 'error', text: '유효기일이 지났습니다.' })
  } else if (l.days_until_expiry != null && l.days_until_expiry >= 0 && l.days_until_expiry <= 7
    && !['SETTLED', 'CANCELLED'].includes(l.status)) {
    a.push({ type: 'warning', text: `유효기일까지 ${l.days_until_expiry}일 남았습니다.` })
  }
  if (l.days_until_shipment != null && l.days_until_shipment >= 0 && l.days_until_shipment <= 7
    && l.status === 'ISSUED') {
    a.push({ type: 'warning', text: `최종선적일까지 ${l.days_until_shipment}일 — 선적 진행을 확인하세요.` })
  }
  return a
})

const fetchData = async () => {
  loading.value = true
  try {
    const response = await axiosInstance.get(`lcs/${lcId.value}/`)
    lc.value = response.data
    try {
      const s = await axiosInstance.get(`lcs/${lcId.value}/settlement_preview/`)
      settlement.value = s.data
    } catch (e) {
      settlement.value = null
    }
  } catch (error) {
    console.error('Failed to fetch LC:', error)
    ElMessage.error('LC 정보를 불러오는데 실패했습니다.')
  } finally {
    loading.value = false
  }
}

// ── 매입 차수 (2026-07-14) — 유산스이자는 같은 매입일 묶음에 부과, 상환 = 차수당 1건 ──
const negDocVisible = ref(false)
const curNeg = ref(null)
const negDocs = ref([])
const negDocForm = reactive({ document_type: 'OTHER', document_number: '', issue_date: '' })
const negDocFile = ref(null)
const negDocFileRef = ref(null)
const negDocUploading = ref(false)

const showSyncMessages = (sync) => {
  if (!sync) return
  if (sync.created?.length) ElMessage.success(`상환 자금계획 생성: ${sync.created.join(', ')}`)
  if (sync.updated?.length) ElMessage.success(`상환 자금계획 갱신: ${sync.updated.join(', ')}`)
  ;(sync.warnings || []).forEach(w => ElMessage.warning(w))
}

const addNegotiation = async () => {
  let value
  try {
    const r = await ElMessageBox.prompt(
      '매입일을 입력하세요. (같은 매입일에 인수된 송장들이 이 차수로 묶입니다)',
      '매입 차수 추가',
      {
        confirmButtonText: '추가',
        cancelButtonText: '취소',
        inputValue: new Date().toISOString().slice(0, 10),
        inputPattern: /^\d{4}-\d{2}-\d{2}$/,
        inputErrorMessage: 'YYYY-MM-DD 형식으로 입력하세요',
      }
    )
    value = r.value
  } catch { return }
  try {
    const res = await axiosInstance.post('lc-negotiations/', {
      lc: lc.value.id, negotiation_date: value,
    })
    ElMessage.success('매입 차수가 추가되었습니다.')
    showSyncMessages(res.data?.sync)
    fetchData()
  } catch (e) {
    const d = e.response?.data
    ElMessage.error(d?.non_field_errors?.[0] || d?.detail || '차수 추가에 실패했습니다.')
  }
}

const editInterest = async (row) => {
  let value
  try {
    const r = await ElMessageBox.prompt(
      `${row.negotiation_date} 매입분 유산스이자 (${row.currency} — 원금과 합산되어 상환액이 갱신됩니다. 없으면 0)`,
      '유산스이자 입력',
      {
        confirmButtonText: '저장',
        cancelButtonText: '취소',
        inputValue: row.usance_interest != null ? String(row.usance_interest) : '',
        inputPattern: /^\d+(\.\d{1,2})?$/,
        inputErrorMessage: '숫자로 입력하세요 (소수점 2자리까지)',
      }
    )
    value = r.value
  } catch { return }
  try {
    const res = await axiosInstance.patch(`lc-negotiations/${row.id}/`, {
      usance_interest: Number(value),
    })
    ElMessage.success('유산스이자가 저장되었습니다.')
    showSyncMessages(res.data?.sync)
    fetchData()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '저장에 실패했습니다.')
  }
}

const deleteNegotiation = async (row) => {
  try {
    await ElMessageBox.confirm(
      `${row.negotiation_date} 매입 차수를 삭제하시겠습니까?\n연결된 상환 예정은 취소되고, 소속 송장은 차수에서 해제됩니다.`,
      '차수 삭제',
      { type: 'warning' }
    )
  } catch { return }
  try {
    await axiosInstance.delete(`lc-negotiations/${row.id}/`)
    ElMessage.success('삭제되었습니다.')
    fetchData()
  } catch (e) {
    const d = e.response?.data
    ElMessage.error((Array.isArray(d) ? d[0] : d?.detail) || '삭제에 실패했습니다.')
  }
}

// 매입 서류 관리
const openNegDocs = (row) => {
  curNeg.value = row
  negDocs.value = row.documents || []
  negDocForm.document_type = 'OTHER'
  negDocForm.document_number = ''
  negDocForm.issue_date = ''
  negDocFile.value = null
  negDocVisible.value = true
}

const refreshNegDocs = async () => {
  if (!curNeg.value) return
  try {
    const res = await axiosInstance.get('lc-negotiation-documents/', {
      params: { negotiation: curNeg.value.id },
    })
    negDocs.value = res.data.results || res.data
  } catch (e) {
    console.error('서류 목록 조회 실패:', e)
  }
}

const onNegDocFile = (e) => {
  negDocFile.value = e.target.files?.[0] || null
}

const uploadNegDoc = async () => {
  if (!negDocForm.document_number && !negDocFile.value) {
    ElMessage.warning('서류번호 또는 파일을 입력하세요.')
    return
  }
  negDocUploading.value = true
  try {
    const fd = new FormData()
    fd.append('negotiation', curNeg.value.id)
    fd.append('document_type', negDocForm.document_type)
    if (negDocForm.document_number) fd.append('document_number', negDocForm.document_number)
    if (negDocForm.issue_date) fd.append('issue_date', negDocForm.issue_date)
    if (negDocFile.value) fd.append('file', negDocFile.value)
    await axiosInstance.post('lc-negotiation-documents/', fd)
    ElMessage.success('서류가 추가되었습니다.')
    negDocForm.document_number = ''
    negDocForm.issue_date = ''
    negDocFile.value = null
    if (negDocFileRef.value) negDocFileRef.value.value = ''
    refreshNegDocs()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '서류 추가에 실패했습니다.')
  } finally {
    negDocUploading.value = false
  }
}

const deleteNegDoc = async (row) => {
  try {
    await axiosInstance.delete(`lc-negotiation-documents/${row.id}/`)
    ElMessage.success('삭제되었습니다.')
    refreshNegDocs()
  } catch (e) {
    ElMessage.error('삭제에 실패했습니다.')
  }
}

const closeNegDocs = () => {
  negDocVisible.value = false
  fetchData()  // 서류 건수 갱신
}

// 서류 인수 처리 (2026-07-09) — 인수일 저장 → 백엔드 훅이 USANCE 상환 만기를 인수일 기준 재계산
const acceptInvoice = async (row) => {
  let value
  try {
    const res = await ElMessageBox.prompt(
      `송장 ${row.invoice_number}의 선적서류 인수일(매입일)을 입력하세요.\n같은 매입일의 송장들은 매입 차수로 묶이고, 상환은 차수 단위(원금+이자)로 생성됩니다.`,
      '서류 인수 처리',
      {
        confirmButtonText: '인수',
        cancelButtonText: '취소',
        inputValue: new Date().toISOString().slice(0, 10),
        inputPattern: /^\d{4}-\d{2}-\d{2}$/,
        inputErrorMessage: 'YYYY-MM-DD 형식으로 입력하세요',
      }
    )
    value = res.value
  } catch {
    return
  }
  try {
    await axiosInstance.patch(`import-invoices/${row.id}/`, { acceptance_date: value })
    ElMessage.success('인수 처리되었습니다. 상환 만기가 인수일 기준으로 갱신됩니다.')
    fetchData()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '인수 처리에 실패했습니다.')
  }
}

const handleStatusAction = async (action) => {
  const messages = {
    'request_issue': '개설 요청하시겠습니까?',
    'confirm_issue': '개설 완료 처리하시겠습니까?',
    'mark_shipped': '선적 완료 처리하시겠습니까?',
    'mark_negotiated': '매입 완료 처리하시겠습니까?',
    'mark_settled': '결제 완료 처리하시겠습니까?'
  }

  try {
    let body = {}
    if (action === 'confirm_issue') {
      // 개설일 지정 (2026-07-14 요청 #11) — 과거 건 이관 시 개설일이 오늘로 찍히던 문제.
      // 기본값: 폼에서 미리 입력한 개설일 > 오늘. 백엔드(views confirm_issue)는 issue_date를 이미 수용.
      const res = await ElMessageBox.prompt(
        '개설일을 입력하세요. (과거 개설 건은 실제 개설일로 수정)',
        '개설 완료',
        {
          confirmButtonText: '개설 완료',
          cancelButtonText: '취소',
          inputValue: lc.value?.issue_date || new Date().toISOString().slice(0, 10),
          inputPattern: /^\d{4}-\d{2}-\d{2}$/,
          inputErrorMessage: 'YYYY-MM-DD 형식으로 입력하세요',
        }
      )
      body = { issue_date: res.value }
    } else {
      await ElMessageBox.confirm(messages[action], '확인')
    }

    const res = await axiosInstance.post(`lcs/${lcId.value}/${action}/`, body)
    ElMessage.success('상태가 변경되었습니다.')
    const d = res?.data || {}
    if (d.warning) ElMessage.warning(d.warning)
    if (d.payment_plan_created?.length) {
      ElMessage.success(`자금계획 ${d.payment_plan_created.length}건 생성 (${d.payment_plan_created.join(', ')})`)
    }
    if (d.plan_warnings?.length) {
      d.plan_warnings.forEach(w => ElMessage.warning(w))
    }
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('상태 변경에 실패했습니다.')
    }
  }
}

const openAmendmentDialog = () => {
  amendmentForm.amendment_date = new Date().toISOString().split('T')[0]
  amendmentForm.description = ''
  amendmentForm.changes = blankChanges()
  showAmendmentDialog.value = true
}

const addAmendment = async () => {
  // 입력된(변경 안 함이 아닌) 항목만 수집 + 현재값과 다른 것만
  const changes = {}
  for (const f of amendableFields) {
    let v = amendmentForm.changes[f.key]
    if (v === undefined || v === null || v === '') continue
    const cur = lc.value?.[f.key]
    if (f.type === 'bool') {
      v = (v === 'true' || v === true)
      if (Boolean(cur) === v) continue
    } else if (f.type === 'number') {
      if (Number(cur) === Number(v)) continue
    } else {
      if (String(cur ?? '') === String(v)) continue
    }
    changes[f.key] = v
  }
  if (Object.keys(changes).length === 0) {
    ElMessage.warning('변경할 항목을 입력하세요.')
    return
  }
  try {
    const res = await axiosInstance.post(`lcs/${lcId.value}/add_amendment/`, {
      amendment_date: amendmentForm.amendment_date,
      description: amendmentForm.description,
      changes,
    })
    ElMessage.success('조건변경이 적용되었습니다.')
    if (res?.data?.warning) ElMessage.warning(res.data.warning)
    showAmendmentDialog.value = false
    fetchData()
  } catch (error) {
    ElMessage.error(error?.response?.data?.error || '조건변경 추가에 실패했습니다.')
  }
}

const goBack = () => {
  router.push('/lcs')
}

// 이 LC의 수입송장 신규 입력 (2026-07-14 요청 #3) — 수입송장 화면이 쿼리를 읽어
// 신규 다이얼로그 자동 오픈 + LC·법인 프리셋 + 'LC로 돌아가기' 버튼 표시
const goNewInvoice = () => {
  router.push({
    path: '/import-invoices',
    query: { lc: lc.value.id, lc_number: lc.value.lc_number, new: 1 },
  })
}

const showHelp = () => {
  helpVisible.value = true
}

const openEditDialog = () => {
  showEditDialog.value = true
}

const onEdited = () => {
  showEditDialog.value = false
  fetchData()
}

onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.lc-detail-view {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-left h2 {
  margin: 0;
}

.header-right {
  display: flex;
  gap: 10px;
}

/* 재설계 Stage 1 요약 영역 */
.lc-summary { margin-bottom: 20px; }
.lc-cta-row { margin-bottom: 14px; }

.lc-stepper {
  display: flex;
  align-items: flex-start;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 14px;
}
.lc-step { display: flex; flex-direction: column; align-items: center; gap: 5px; flex: 1; }
.lc-step .dot {
  width: 30px; height: 30px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; background: #e4e7ed; color: #909399;
}
.lc-step.done .dot { background: #67c23a; color: #fff; }
.lc-step.current .dot { background: #409eff; color: #fff; }
.lc-step .lbl { font-size: 13px; color: #909399; }
.lc-step.done .lbl, .lc-step.current .lbl { color: #303133; font-weight: 500; }
.lc-step .dt { font-size: 11px; color: #c0c4cc; }
.lc-line { flex: 0 0 28px; height: 2px; background: #e4e7ed; margin-top: 14px; }
.lc-line.done { background: #67c23a; }

.lc-kpi { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 14px; }
.lc-kpi .kpi { background: #f5f7fa; border-radius: 8px; padding: 12px 14px; }
.lc-kpi .k { font-size: 12px; color: #909399; margin-bottom: 4px; }
.lc-kpi .v { font-size: 18px; font-weight: 500; color: #303133; }
.lc-kpi .v.dday-warn { color: #e6a23c; }
.lc-kpi .v.dday-danger { color: #f56c6c; }

.lc-progress {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  background: #f5f7fa; border-radius: 8px; padding: 14px 18px; margin-bottom: 14px;
}
.lc-progress .prg-head {
  display: flex; justify-content: space-between; align-items: baseline;
  font-size: 13px; margin-bottom: 6px;
}
.lc-progress .prg-head > span:first-child { font-weight: 500; color: #303133; }
.lc-progress .prg-num { color: #606266; font-size: 12px; }

.lc-alert {
  display: flex; align-items: center; gap: 6px;
  border-radius: 6px; padding: 8px 12px; margin-bottom: 8px; font-size: 13px;
}
.lc-alert.warning { background: #fdf6ec; color: #e6a23c; }
.lc-alert.error { background: #fef0f0; color: #f56c6c; }

@media (max-width: 768px) {
  .lc-kpi { grid-template-columns: repeat(2, 1fr); }
}
.voucher-line { display: flex; align-items: center; gap: 10px; padding: 2px 0; }

/* 매입 차수 (2026-07-14) */
.neg-hint { font-size: 12px; color: #909399; margin-top: 8px; }
.neg-none { color: #c0c4cc; }
.neg-inv-cnt { font-size: 11px; color: #909399; margin-left: 6px; cursor: help; }
.neg-doc-form { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.neg-doc-file { font-size: 12px; max-width: 200px; }
.voucher-line .v-doc { font-weight: 600; }
.voucher-line .v-amt { color: #606266; font-size: 0.85rem; }

/* 조건변경(Amendment) 다이얼로그 그리드 */
.amd-hint { font-size: 0.8rem; color: #909399; margin: 4px 0 8px; }
.amd-grid { border: 1px solid #ebeef5; border-radius: 6px; overflow: hidden; }
.amd-grid-head, .amd-grid-row {
  display: grid; grid-template-columns: 120px 1fr 1.4fr;
  align-items: center; gap: 10px; padding: 6px 10px;
}
.amd-grid-head { background: #f5f7fa; font-size: 0.78rem; color: #909399; }
.amd-grid-row { border-top: 1px solid #f0f2f5; }
.amd-fld { font-size: 0.85rem; color: #303133; }
.amd-cur { font-size: 0.82rem; color: #909399; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* 조건변경 이력 old→new */
.amd-changes { margin-top: 4px; }
.amd-change { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; padding: 1px 0; }
.amd-change .amd-label { min-width: 76px; color: #606266; }
.amd-change .amd-old { color: #909399; text-decoration: line-through; }
.amd-change .amd-arrow { color: #c0c4cc; }
.amd-change .amd-new { color: #303133; font-weight: 600; }
</style>
